import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const TRACKS = {
  mentor: {
    label: '1V1 導師諮詢',
    icon: 'ri-user-star-line',
    steps: [
      {
        num: '01',
        title: '瀏覽精選導師',
        desc: '從頂尖科技公司、AI 獨角獸及跨國顧問機構的內部專家中，按領域、背景、價格篩選最適合你的職涯導師',
        icon: 'ri-search-eye-line',
        color: 'bg-violet-50',
        iconColor: 'text-violet-600',
      },
      {
        num: '02',
        title: '預約專屬時段',
        desc: '透過互動行事曆系統，彈性選擇適合你時區的 1 對 1 諮詢時段，即時確認預約，48 小時內導師確認回應',
        icon: 'ri-calendar-check-line',
        color: 'bg-slate-50',
        iconColor: 'text-[#0A1628]',
      },
      {
        num: '03',
        title: '深度 1 對 1 諮詢',
        desc: '透過高清視訊會議進行個人化職涯規劃、面試衝刺或履歷精修，會議全程可錄製，附詳細會後追蹤報告',
        icon: 'ri-video-chat-line',
        color: 'bg-violet-50',
        iconColor: 'text-violet-600',
      },
      {
        num: '04',
        title: '拿下目標 Offer',
        desc: '95% 的學員在 3 個月內成功進入目標外商。導師持續陪伴，直到你踏上全球 AI 職涯新篇章',
        icon: 'ri-trophy-line',
        color: 'bg-slate-50',
        iconColor: 'text-[#0A1628]',
      },
    ],
    cta: { label: '立即預約導師', path: '/mentors', color: 'bg-violet-600 hover:bg-violet-700' },
  },
  course: {
    label: '線上課程學習',
    icon: 'ri-book-open-line',
    steps: [
      {
        num: '01',
        title: '挑選心儀課程',
        desc: '按 AI 方向、難度等級和主題篩選，快速找到適合你當前階段的精選課程，每門均由外商內部專家親授',
        icon: 'ri-search-line',
        color: 'bg-amber-50',
        iconColor: 'text-amber-600',
      },
      {
        num: '02',
        title: '一鍵購課',
        desc: '填寫資料、完成付款，全程 2 分鐘內搞定。課程內容立即解鎖，可隨時在任何裝置上存取',
        icon: 'ri-shopping-cart-line',
        color: 'bg-slate-50',
        iconColor: 'text-[#0A1628]',
      },
      {
        num: '03',
        title: '按節奏系統學習',
        desc: '影片課程可隨時暫停、重播，永久有效不過期。搭配練習題、專案案例和學員社群，學完即可上手',
        icon: 'ri-play-circle-line',
        color: 'bg-amber-50',
        iconColor: 'text-amber-600',
      },
      {
        num: '04',
        title: '獲得結課證書',
        desc: '完成所有學習模組後，頒發 NexusGo 認證結課證書，可直接新增至 LinkedIn，提升職場競爭力',
        icon: 'ri-award-line',
        color: 'bg-slate-50',
        iconColor: 'text-[#0A1628]',
      },
    ],
    cta: { label: '瀏覽全部課程', path: '/courses', color: 'bg-amber-500 hover:bg-amber-600' },
  },
};

const HowItWorks = () => {
  const [activeTrack, setActiveTrack] = useState<'mentor' | 'course'>('mentor');
  const navigate = useNavigate();
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation<HTMLDivElement>({ threshold: 0.2 });
  const { ref: stepsRef, isVisible: stepsVisible } = useScrollAnimation<HTMLDivElement>({ threshold: 0.1 });

  const track = TRACKS[activeTrack];

  return (
    <section className="py-16 sm:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">

        {/* Header */}
        <div
          ref={headerRef}
          className="text-center mb-8 sm:mb-12"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(28px)',
            transition: 'opacity 0.7s cubic-bezier(0.22,1,0.36,1), transform 0.7s cubic-bezier(0.22,1,0.36,1)',
          }}
        >
          <span className="text-xs font-semibold text-violet-600 tracking-widest uppercase mb-3 block">
            兩條路徑，一個目標
          </span>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#0A1628] mb-4 tracking-tight">
            選擇最適合你的成長方式
          </h2>
          <p className="text-slate-500 max-w-xl mx-auto text-sm leading-relaxed mb-7">
            無論是系統學習還是 1 對 1 深度諮詢，NexusGo 為你量身匹配
          </p>

          {/* Track Switcher */}
          <div className="inline-flex bg-slate-100 rounded-full p-1 gap-1">
            {(Object.keys(TRACKS) as Array<'mentor' | 'course'>).map((key) => (
              <button
                key={key}
                onClick={() => setActiveTrack(key)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold cursor-pointer transition-all whitespace-nowrap ${
                  activeTrack === key
                    ? key === 'mentor'
                      ? 'bg-violet-600 text-white'
                      : 'bg-amber-500 text-white'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                <i className={TRACKS[key].icon} />
                {TRACKS[key].label}
              </button>
            ))}
          </div>
        </div>

        {/* Steps */}
        <div ref={stepsRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 relative">
          <div className={`absolute top-8 left-[12.5%] right-[12.5%] h-px hidden lg:block transition-colors duration-500 ${
            activeTrack === 'mentor'
              ? 'bg-gradient-to-r from-transparent via-violet-200 to-transparent'
              : 'bg-gradient-to-r from-transparent via-amber-200 to-transparent'
          }`} />

          {track.steps.map((step, i) => (
            <div
              key={`${activeTrack}-${i}`}
              className="flex flex-col items-center text-center group"
              style={{
                opacity: stepsVisible ? 1 : 0,
                transform: stepsVisible ? 'translateY(0)' : 'translateY(36px)',
                transition: 'opacity 0.65s cubic-bezier(0.22,1,0.36,1), transform 0.65s cubic-bezier(0.22,1,0.36,1)',
                transitionDelay: `${i * 120}ms`,
              }}
            >
              <div className={`relative w-16 h-16 flex items-center justify-center rounded-2xl ${step.color} mb-5 group-hover:scale-105 transition-transform`}>
                <i className={`${step.icon} text-2xl ${step.iconColor}`} />
                <span className={`absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center text-white text-xs font-bold rounded-full ${
                  activeTrack === 'mentor' ? 'bg-violet-600' : 'bg-amber-500'
                }`}>
                  {i + 1}
                </span>
              </div>
              <h3 className="text-base font-bold text-[#0A1628] mb-3">{step.title}</h3>
              <p className="text-slate-500 text-sm leading-relaxed">{step.desc}</p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div
          className="mt-12 text-center"
          style={{
            opacity: stepsVisible ? 1 : 0,
            transform: stepsVisible ? 'translateY(0)' : 'translateY(16px)',
            transition: 'opacity 0.6s ease 0.5s, transform 0.6s ease 0.5s',
          }}
        >
          <button
            onClick={() => navigate(track.cta.path)}
            className={`px-8 py-3.5 ${track.cta.color} text-white text-sm font-semibold rounded-lg cursor-pointer whitespace-nowrap transition-all inline-flex items-center gap-2`}
          >
            {track.cta.label}
            <i className="ri-arrow-right-line" />
          </button>
        </div>

      </div>
    </section>
  );
};

export default HowItWorks;
